/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable fp/no-loops */
/* eslint-disable @typescript-eslint/prefer-for-of */
/* eslint-disable fp/no-mutating-methods */
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js'
import { Bar } from 'react-chartjs-2'

import { ContainerHomGradientBorder } from '~/components/Containers'
import { ONBOARDING_PICTURES } from '~/onboarding/onboarding.constants'
import { getImageUrl } from '~/components/Image/helpers'
import SVGImage from '~/components/Image/SVGImage'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface Props {
  setShowBarChartModal: Dispatch<SetStateAction<number>>
  testNo: number
  attempts: Array<{ correctnessPercentage: number; date: string }>
}

export default function ScoreboardTestPrepChart(props: Props): React.ReactElement {
  const { t } = useTranslation(['statistics', 'generics'])

  const { setShowBarChartModal, testNo, attempts } = props
  const [dataPoints, setDataPoints] = useState<Array<number | null>>([])
  const [labels, setLabels] = useState<Array<string | null>>([])
  const [bestAttempt, setBestAttempt] = useState<{ index: number; data: number } | null>(null)

  // Handle screen size
  const [barThickness, setBarThickness] = useState(32)
  const [gradientBoxWidth, setGradientBoxWidth] = useState('556px')
  const [canvasHeight, setCanvasHeight] = useState('395px')
  const [canvasWidth, setCanvasWidth] = useState('436px')
  const [canvasPaddingTop, setCanvasPaddingTop] = useState(130)
  const [yTickFontSize, setYTickFontSize] = useState(16)
  const [xAxisTickDisplay, setXaxisTickDisplay] = useState(!!(dataPoints && dataPoints.length > 0))

  useEffect(() => {
    if (attempts.length > 0) {
      const attemptValues: Array<number | null> = attempts.map((attempt) => attempt.correctnessPercentage)
      const attemptDates: Array<string | null> = attempts.map((attempt) =>
        new Date(attempt.date).toISOString().slice(0, 10),
      )
      attemptValues.unshift(null)
      attemptValues.push(null)
      attemptDates.unshift(null)
      attemptDates.push(null)
      const bestAttemptIndex: number = attemptValues.reduce((bestIndex, value, index) => {
        if (
          bestIndex === -1 ||
          bestIndex === null ||
          attemptValues[bestIndex] === null ||
          (value !== null && value > attemptValues[bestIndex])
        ) {
          return index
        }
        return bestIndex
      }, -1)
      const bestAttemptItem =
        bestAttemptIndex >= 0 ? { index: bestAttemptIndex, data: attemptValues[bestAttemptIndex] } : {}
      setBestAttempt(bestAttemptItem)
      setDataPoints(attemptValues)
      setLabels(attemptDates)
      setXaxisTickDisplay(attemptValues.length > 2)
    } else {
      setBestAttempt(null)
      setDataPoints([])
      setLabels([])
      setXaxisTickDisplay(false)
    }
  }, [attempts])

  const checkScreenSize = () => {
    if (window.innerWidth >= 768) {
      // Large screen
      setBarThickness(32)
      setGradientBoxWidth('556px')
      setCanvasHeight('395px')
      setCanvasPaddingTop(130)
      setYTickFontSize(16)
    } else {
      // Small screen
      setBarThickness(20)
      setGradientBoxWidth('350px')
      setCanvasHeight('378px')
      setCanvasPaddingTop(100)
      setYTickFontSize(12)
    }

    if (dataPoints && dataPoints.length > 2) {
      if (window.innerWidth >= 768) {
        setCanvasWidth('436px')
      } else if (window.innerWidth >= 360) {
        setCanvasWidth('275px')
      } else {
        setCanvasWidth('270px')
      }
    } else if (dataPoints.length <= 2) {
      if (window.innerWidth >= 768) {
        setCanvasWidth('426px')
      } else {
        setCanvasWidth('265px')
      }
    }
  }

  useEffect(() => {
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [dataPoints])

  // Gardiant home box styles
  const shadows = {
    boxShadow: '-1.76144px 5.28432px 12.3301px rgba(0, 0, 0, 0.15)',
    maxWidth: gradientBoxWidth,
    width: '100%',
    maxHeight: '438px',
    margin: 'auto',
  }

  const getGardient = (context: any) => {
    const chart = context.chart
    const chartArea = chart.chartArea
    if (!chartArea) {
      return '#F4ECFC'
    }

    if (bestAttempt && bestAttempt?.index === context.dataIndex) {
      const gardient = chart.ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
      gardient.addColorStop(0.3, '#467AFF')
      gardient.addColorStop(0.6, '#FB0D98')
      gardient.addColorStop(0.9, '#FF9B05')
      return gardient
    }

    return '#F4ECFC'
  }

  const getHoverGardient = (context: any) => {
    const chart = context.chart
    const chartArea = chart.chartArea
    if (!chartArea) {
      return '#7D38D6'
    }

    if (bestAttempt && bestAttempt.index === context.dataIndex) {
      const gardient = chart.ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
      gardient.addColorStop(0.3, '#467AFF')
      gardient.addColorStop(0.6, '#FB0D98')
      gardient.addColorStop(0.9, '#FF9B05')
      return gardient
    } else if (context.dataIndex === 3) {
      const gardient = chart.ctx.createLinearGradient(0, 0, 290, 0)
      gardient.addColorStop(0.7, '#8E4FD6')
      gardient.addColorStop(0.9, '#6F62E8')
      return gardient
    } else if (context.dataIndex === 4) {
      const gardient = chart.ctx.createLinearGradient(0, 0, 390, 0)
      gardient.addColorStop(0.7, '#8E4FD6')
      gardient.addColorStop(0.9, '#6F62E8')
      return gardient
    } else if (context.dataIndex === 5) {
      const gardient = chart.ctx.createLinearGradient(0, 0, 490, 0)
      gardient.addColorStop(0.7, '#8E4FD6')
      gardient.addColorStop(0.9, '#6F62E8')
      return gardient
    } else {
      const gardient = chart.ctx.createLinearGradient(0, 0, context.dataIndex * 100, 0)
      gardient.addColorStop(0.7, '#8E4FD6')
      gardient.addColorStop(1, '#6F62E8')
      return gardient
    }
  }

  const getTooltipGardient = (context: any) => {
    const chart = context.chart
    const tooltipIndex = context?.tooltip?.dataPoints[0]?.dataIndex
    const chartArea = chart.chartArea
    if (!chartArea) {
      return '#6F62E8'
    }

    let gardient
    if (tooltipIndex === 1) {
      gardient = chart.ctx.createLinearGradient(0, 0, 100, 0)
      gardient.addColorStop(0.6, '#8E4FD6')
      gardient.addColorStop(1, '#6F62E8')
    } else if (tooltipIndex === 2) {
      gardient = chart.ctx.createLinearGradient(0, 0, 200, 0)
      gardient.addColorStop(0.6, '#8E4FD6')
      gardient.addColorStop(1, '#6F62E8')
    } else if (tooltipIndex === 3) {
      gardient = chart.ctx.createLinearGradient(0, 0, 280, 0)
      gardient.addColorStop(0.6, '#8E4FD6')
      gardient.addColorStop(1, '#6F62E8')
    } else if (tooltipIndex === 4) {
      gardient = chart.ctx.createLinearGradient(0, 0, 420, 0)
      gardient.addColorStop(0.6, '#8E4FD6')
      gardient.addColorStop(1, '#6F62E8')
    } else if (tooltipIndex === 5) {
      gardient = chart.ctx.createLinearGradient(0, 0, 450, 0)
      gardient.addColorStop(0.7, '#8E4FD6')
      gardient.addColorStop(1, '#6F62E8')
    } else {
      gardient = chart.ctx.createLinearGradient(0, 0, tooltipIndex * 100, 0)
      gardient.addColorStop(0.6, '#8E4FD6')
      gardient.addColorStop(1, '#6F62E8')
    }

    return gardient
  }

  // Graph options & properties
  const chartOptions = {
    maintainAspectRatio: false,
    defaultFontFamily: 'Poppins',
    layout: {
      padding: {
        top: canvasPaddingTop,
        bottom: xAxisTickDisplay ? 0 : 62,
      },
    },
    barThickness,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const,
      },
      title: {
        display: false,
        text: t('statistics:last_attempts'),
      },
      tooltip: {
        // intersect: false,
        yAlign: 'bottom' as const,
        cornerRadius: 16,
        height: 44,
        width: 76,
        padding: 16,
        backgroundColor: (context: any) => {
          return getTooltipGardient(context)
        },
        displayColors: false,
        caretSize: 12,
        caretPadding: (tooltip: any) => {
          const tooltipIndex = tooltip?.tooltipItems[0]?.dataIndex
          return bestAttempt && bestAttempt.index === tooltipIndex ? 20 : 5
        },
        bodyFont: {
          family: 'Poppins',
          size: 16,
          weight: 600,
        },
        callbacks: {
          title() {
            return ''
          },
          label(context: any) {
            let label = context.dataset.label || ''
            if (context.parsed.y !== null) {
              label += `  ${context.parsed.y}%  `
            }
            return label
          },
        },
      },
    },
    scales: {
      x: {
        offset: false,
        title: {
          display: false,
          text: 'Attempts',
          color: '#474141',
          position: 'start',
        },
        ticks: {
          display: xAxisTickDisplay,
          maxRotation: 45,
          minRotation: 45,
          color: '#474141',
          font: {
            family: 'Poppins',
            size: 12,
            weight: 400,
            // lineHeight: 1.8,
          },
        },
        grid: {
          display: false,
          tickLength: 0,
        },
      },
      y: {
        min: 0,
        max: 100,
        title: {
          display: false,
          text: '% correct',
          color: '#474141',
          position: 'end',
        },
        ticks: {
          stepSize: 10,
          maxTicksLimit: 12,
          autoSkip: false,
          color: '#474141',
          font: {
            family: 'Poppins',
            size: yTickFontSize,
            weight: 400,
            // lineHeight: 1.8,
          },
        },
        grid: {
          color: 'rgba(125, 56, 214, 0.2)',
          borderWidth: 1,
        },
      },
    },
  }

  const chartData = {
    labels,
    datasets: [
      {
        label: '',
        data: dataPoints,
        backgroundColor: (context: any) => {
          return getGardient(context)
        },
        hoverBackgroundColor: (context: any) => {
          return getHoverGardient(context)
        },
        borderColor: '#7D38D6',
        borderWidth: 1,
        borderRadius: 3,
        barPercentage: 0.1,
        categoryPercentage: 0.9,
      },
    ],
  }

  useEffect(() => {
    const beforeDrawPluginId = `beforeDrawChartBar${testNo}${new Date().getTime()}`
    const afterDrawPluginId = `afterDrawChartBar${testNo}${new Date().getTime()}`

    // Define and register the plugins
    const beforeDrawPlugin = {
      id: beforeDrawPluginId,
      beforeDraw(chart: any) {
        const {
          ctx,
          scales: { x, y },
        } = chart
        ctx.save()

        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
        ctx.restore()

        let yTickPosition
        let crownIconWidth
        let crownIconHeight
        let crownXminus
        let crownYminus

        if (window.innerWidth >= 768) {
          yTickPosition = 31
          crownIconWidth = 29.65
          crownIconHeight = 19.77
          crownXminus = 15
          crownYminus = 20
        } else {
          yTickPosition = 25
          crownIconWidth = 18.15
          crownIconHeight = 12.1
          crownXminus = 9
          crownYminus = 12
        }

        for (let i = 0; i < y._labelSizes.widths.length; i++) {
          ctx.fillStyle = '#7D38D6'
          ctx.fillRect(yTickPosition, y.getPixelForTick(i) - 0.5, 8.5, 1)
          ctx.restore()
        }

        if (dataPoints && dataPoints.length > 2 && bestAttempt) {
          const crownImg = document.createElement('img')
          crownImg.src = getImageUrl(
            crownIconWidth,
            crownIconWidth,
            'cover',
            'scoreboard/crown.svg',
            'hom-frontend-assets',
          ).urlWithFormat

          crownImg.onload = () => {
            ctx.drawImage(
              crownImg,
              x.getPixelForValue(bestAttempt.index) - crownXminus,
              y.getPixelForValue(bestAttempt.data) - crownYminus,
              crownIconWidth,
              crownIconHeight,
            )
          }
        }
      },
    }

    const afterDrawPlugin = {
      id: afterDrawPluginId,
      afterDraw(chart: any) {
        const chartArea = chart.chartArea
        const ctx = chart.ctx
        ctx.save()

        // Set 4 sides color
        ctx.strokeStyle = '#7D38D6'
        ctx.lineWidth = 1

        // Draw 2 lines
        // Left
        ctx.beginPath()
        ctx.moveTo(chartArea.left, chartArea.top)
        ctx.lineTo(chartArea.left, chartArea.bottom)
        ctx.stroke()
        // Bottom
        ctx.beginPath()
        ctx.moveTo(chartArea.right, chartArea.bottom)
        ctx.lineTo(chartArea.left, chartArea.bottom)
        ctx.stroke()
        // Restore
        ctx.restore()
      },
    }

    // Register the plugins
    ChartJS.register(beforeDrawPlugin, afterDrawPlugin)

    // Cleanup function to deregister plugins
    return () => {
      ChartJS.unregister(beforeDrawPlugin, afterDrawPlugin)
    }
  }, [dataPoints, bestAttempt])

  return (
    <aside className="fixed inset-0 z-50 bg-white/80 backdrop-filter backdrop-blur-sm">
      <section className="h-full flex items-center justify-center">
        <ContainerHomGradientBorder
          borderThickness={2}
          gradientType="statistics"
          innerClassName="flex flex-col bg-white rounded pl-1.5 sm360:pl-5 md:pl-[44.11px] pr-[14.45px] md:pr-[13.5px] pt-6 md:pt-3.5 pb-2 md:pb-1.5 relative"
          outerClassName="shadow-sm"
          outerStyle={{ ...shadows }}
        >
          <div className="flex flex-col justify-center items-center absolute w-[85%] top-6 md:top-5">
            <h3 className="text-center text-3 md:text-md font-semibold text-black-light font-sans leading-4 md:leading-7 h-3.5 md:h-4.5">
              {t('statistics:testWithNumber', { n: testNo })}
            </h3>
            <h2 className="text-center text-md md:text-8 font-semibold text-black-light font-sans leading-7 md:leading-12 h-7 md:h-12">
              {t('statistics:last_attempts')}
            </h2>
          </div>
          <div>
            <div className="absolute left-5 md:left-12 top-21 md:top-25 text-3 md:text-base font-semibold text-black-light font-sans leading-4.5 md:leading-6 h-4.5 md:h-6">
              % {t('statistics:correct')}
            </div>
            <button
              className="flex items-center space-x-2 blue absolute right-4 top-4 h-3 w-3"
              onClick={() => setShowBarChartModal(-1)}
            >
              <SVGImage alt={t('generics:ariaLabelButtons.closeWindow')} srcKey={ONBOARDING_PICTURES.exit} />
            </button>
            <div className="relative" style={{ zIndex: 1, height: `${canvasHeight}`, width: `${canvasWidth}` }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
            <div className="absolute left-[265px] sm360:left-[290px] sm:left-[290px] md:left-[480px] bottom-16 md:bottom-15.5 text-2.5 md:text-3 font-semibold text-black-light font-sans leading-4.5 h-4.5">
              {t('statistics:attempts')}
            </div>
          </div>
        </ContainerHomGradientBorder>
      </section>
    </aside>
  )
}
